import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, map, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { WeightedRegionsService } from '@firebird-web/shared-services';
import { CommonUtils } from '@firebird-web/shared-utils';
import {
  NaIsoContinent,
  WEIGHTED_CONTINENT_PERMISSION_KEY,
} from '../../../../../weighted/src/lib/constants/weighted.constants';
import { IsoNaRegionType } from '../../../../../weighted/src/lib/enums';
import { Continent, Region } from './interfaces';
import { SUMMARY_VIEW } from '@firebird-web/shared-constants';
import { availableFilters } from './weighted-location-selection.constants';
import { Filters } from './weighted-location-selection.types';

@Component({
  selector: 'firebird-web-weighted-location-selection',
  templateUrl: './weighted-location-selection.component.html',
  styleUrls: ['./weighted-location-selection.component.scss'],
  standalone: false,
})
export class WeightedLocationSelectionComponent implements OnInit, OnDestroy {
  public continentPermissionKey = WEIGHTED_CONTINENT_PERMISSION_KEY;
  public selectedRegionType: IsoNaRegionType;
  public continentList!: Continent[];
  public continentId$: BehaviorSubject<string> = new BehaviorSubject('');
  public regionList$: BehaviorSubject<Region[][]> = new BehaviorSubject<
    Region[][]
  >([]);
  public continentText$ = this.continentId$.pipe(
    map(
      (continentId) =>
        this.continentList.find(({ id }) => continentId === id)?.text
    )
  );
  public filter: Filters = availableFilters[0];

  public showRegionTypeToggle$ = this.continentId$.pipe(
    map((continentId) => {
      if (this.data?.hideRegionTypeToggle) {
        return false;
      }

      if (this.data?.regionTypeSwitchesFilter) {
        const allowedContinents: string[] | undefined =
          this.data?.regionTypeSwitchAvailableContinents;
        return !allowedContinents || allowedContinents.includes(continentId);
      }
      return !!this.data?.isWidget && continentId === NaIsoContinent;
    })
  );

  public summaryView: Region | null = null;
  public primaryLens: string;

  private regionsListSource: Region[] = [];

  private readonly destroyed$: Subject<void> = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly dialogRef: MatDialogRef<WeightedLocationSelectionComponent>,
    private readonly weightedRegionsServices: WeightedRegionsService
  ) {
    this.selectedRegionType =
      this.data.selectedRegionType || IsoNaRegionType.Iso;
  }

  ngOnInit(): void {
    this.initLocationData();
    this.watchForContinentChange();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public changeRegionType({ value }: { value: IsoNaRegionType }): void {
    if (value === this.selectedRegionType) {
      return;
    }

    this.selectedRegionType = value;

    if (this.data?.regionTypeSwitchesFilter) {
      this.switchFilterByRegionType(value as unknown as Filters);
      return;
    }

    const filteredRegions = [...this.regionsListSource].filter(
      (region: Region) => region.regionType === value
    );
    this.regionList$.next(CommonUtils.toChunksByColumnsQty(filteredRegions, 2));
  }

  public closeDialog() {
    this.dialogRef.close();
  }

  public onContinentChange(continent: Continent) {
    this.continentId$.next(continent.id);
  }

  public onRegionSelected(region: Region) {
    this.dialogRef.close({
      regionName: region.text,
      regionId: region.id,
      continentId: this.continentId$.getValue(),
      selectedRegionType: this.selectedRegionType,
    });
  }

  public trackByFnGroup(index: number) {
    return index;
  }

  private switchFilterByRegionType(filter: Filters): void {
    this.filter = filter;
    this.continentList =
      this.data?.continentListByFilter?.[filter] ?? this.continentList;

    const currentContinentId = this.continentId$.getValue();
    const isCurrentContinentAvailable = this.continentList.some(
      ({ id }) => id === currentContinentId
    );

    this.continentId$.next(
      isCurrentContinentAvailable
        ? currentContinentId
        : this.continentList[0]?.id ?? ''
    );
  }

  private initLocationData(): void {
    this.filter = this.data.filter;
    this.continentList = this.data.continentList;
    this.primaryLens = this.data.primaryLens;
    const continentId = this.data.continentId;

    this.continentId$.next(continentId);
  }

  private watchForContinentChange(): void {
    this.continentId$
      .pipe(
        switchMap((continentId) => {
          switch (this.filter) {
            case 'iso': {
              return this.weightedRegionsServices.getWeightedIsoRegions(
                continentId,
                this.data?.isWidget
              );
            }
            case 'degree': {
              return this.weightedRegionsServices.getWeightedDegreeDaysRegions(
                continentId
              );
            }
            default: {
              return this.weightedRegionsServices.getWeightedUsGasBcfRegions(
                continentId
              );
            }
          }
        }),
        map((regions) => {
          this.regionsListSource = regions;

          if (
            !this.data?.regionTypeSwitchesFilter &&
            this.data?.isWidget &&
            this.continentId$.value === NaIsoContinent
          ) {
            return regions.filter(
              (region: Region) => region.regionType === this.selectedRegionType
            );
          }
          return regions;
        }),
        map(
          (regions) =>
            CommonUtils.toChunksByColumnsQty(regions, 2) as Region[][]
        ),
        tap(() => {
          if (this.continentId$.value === NaIsoContinent) {
            this.summaryView = SUMMARY_VIEW;
          } else {
            this.summaryView = null;
          }
        }),
        takeUntil(this.destroyed$)
      )
      .subscribe((regions: Region[][]) => {
        this.regionList$.next(regions);
      });
  }
}
